import LegalLayout, { LegalSection } from "../components/LegalLayout";

export default function PrivacyPage() {
  return (
    <LegalLayout title="Privacy Policy" updated="July 3, 2026" testid="privacy-page">
      <p>
        This Privacy Policy explains how <strong>Evervow</strong> ("we", "us", "our") collects, uses, and
        protects your information when you use our dating service — including our website at{" "}
        <a href="https://evervow.me" className="text-[#7A3B3F] underline underline-offset-4">
          evervow.me
        </a>{" "}
        and any mobile applications (together, the "Service"). By using the Service you agree to the
        practices described here.
      </p>

      <LegalSection heading="1. Information we collect">
        <p>To operate a serious, marriage-minded dating experience, we collect the following categories of personal data:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Account & profile information:</strong> name, email, password (stored as a bcrypt hash),
            age, gender, city/location text you enter, bio, values, preferred relationship dynamic
            (e.g. Traditional, 50/50 Partnership, Flexible, Dual Career, Homemaker Supported), and
            intended marriage timeline.
          </li>
          <li>
            <strong>Photos:</strong> profile pictures you choose to upload.
          </li>
          <li>
            <strong>Interactions:</strong> profiles you like or pass, mutual matches, and the content of
            messages you send to matches through Evervow.
          </li>
          <li>
            <strong>Payment information:</strong> if you buy Evervow Premium, our payment processor
            (Stripe) collects and processes your card details. We do <em>not</em> store your full card
            number on our servers — we only store your subscription plan, purchase date, and premium
            expiry date.
          </li>
          <li>
            <strong>Device & log data:</strong> IP address, browser or device type, referring page,
            and timestamps of significant events (e.g. sign-in attempts) for security and abuse
            prevention.
          </li>
          <li>
            <strong>Cookies:</strong> a session cookie (`access_token`) that keeps you signed in.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="2. How we use your information">
        <ul className="list-disc pl-6 space-y-2">
          <li>Create and secure your account and profile.</li>
          <li>Show your profile to other members and show you compatible profiles.</li>
          <li>Enable messaging between mutual matches.</li>
          <li>Process Premium purchases and manage subscription status.</li>
          <li>Prevent fraud, abuse, harassment, and violations of our Terms of Service.</li>
          <li>Communicate with you about your account, safety, and important service updates.</li>
        </ul>
        <p>
          We do <strong>not</strong> sell your personal data, and we do not use your messages or
          photos to train advertising profiles.
        </p>
      </LegalSection>

      <LegalSection heading="3. Third parties we share data with">
        <ul className="list-disc pl-6 space-y-2">
          <li>
            <strong>Stripe</strong> (payments): if you purchase Premium, your payment details are
            collected and processed by Stripe under their own privacy policy at{" "}
            <a href="https://stripe.com/privacy" className="text-[#7A3B3F] underline underline-offset-4">
              stripe.com/privacy
            </a>
            . We share only your user id and the amount/plan being purchased.
          </li>
          <li>
            <strong>Hosting & infrastructure providers</strong> that store our database and serve the
            Service, under contractual data-protection commitments.
          </li>
          <li>
            <strong>Law enforcement or courts</strong> when we are legally required to respond to a
            valid legal process, or when necessary to protect the safety of our members.
          </li>
        </ul>
      </LegalSection>

      <LegalSection heading="4. Data retention">
        <p>
          We keep your profile, matches, and messages for as long as your account is active. When
          you delete your account (see{" "}
          <a href="/delete-account" className="text-[#7A3B3F] underline underline-offset-4">
            Delete your account
          </a>
          ), we remove your profile, photos, swipes, matches, and messages within 30 days, except
          where we must retain limited records for legal, tax, fraud-prevention, or safety reasons
          (for example, records of banned accounts).
        </p>
      </LegalSection>

      <LegalSection heading="5. Your rights">
        <p>Depending on where you live, you may have the right to:</p>
        <ul className="list-disc pl-6 space-y-2">
          <li>Access the personal data we hold about you.</li>
          <li>Correct inaccurate information (you can edit most of it directly on your profile).</li>
          <li>Delete your account and associated data.</li>
          <li>Object to or restrict certain processing.</li>
          <li>Withdraw consent where processing is based on consent.</li>
        </ul>
        <p>
          To exercise any of these rights, email us at{" "}
          <a href="mailto:support@evervow.me" className="text-[#7A3B3F] underline underline-offset-4">
            support@evervow.me
          </a>
          .
        </p>
      </LegalSection>

      <LegalSection heading="6. Children">
        <p>
          Evervow is only for people <strong>18 or older</strong>. We do not knowingly collect any
          personal data from anyone under 18. If we learn that we have collected data from a minor,
          we will delete it and terminate the account.
        </p>
      </LegalSection>

      <LegalSection heading="7. Security">
        <p>
          We hash passwords with bcrypt, transmit data over HTTPS, use httpOnly session cookies, and
          rate-limit sensitive endpoints. No system is perfectly secure, but we work hard to protect
          your information.
        </p>
      </LegalSection>

      <LegalSection heading="8. Changes to this policy">
        <p>
          If we materially change this Privacy Policy, we will update the "Last updated" date above
          and, where appropriate, notify you in the app or by email.
        </p>
      </LegalSection>

      <LegalSection heading="9. Contact us">
        <p>
          Questions or requests? Email{" "}
          <a href="mailto:support@evervow.me" className="text-[#7A3B3F] underline underline-offset-4">
            support@evervow.me
          </a>
          .
        </p>
      </LegalSection>
    </LegalLayout>
  );
}
